import { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import { Plus, PenSquare } from "lucide-react";
import { toast } from "sonner";

export default function Whiteboard() {
  const [boards, setBoards] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "" });

  const load = () => api.get("/whiteboards").then(r => {
    setBoards(r.data);
    if (!active && r.data.length) setActive(r.data[0]);
  });
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title) return toast.error("Title required");
    const r = await api.post("/whiteboards", form);
    toast.success("Whiteboard created");
    setOpen(false);
    setForm({ title: "", content: "" });
    setActive(r.data);
    load();
  };

  const save = async () => {
    if (!active) return;
    await api.patch(`/whiteboards/${active.id}`, { content: active.content });
    toast.success("Saved");
    load();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Whiteboards"
        subtitle="Shared canvases for planning and brainstorming"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="add-board-btn">
                <Plus className="w-4 h-4 mr-1" /> New Board
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Whiteboard</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input placeholder="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="board-title-input" />
                <Textarea rows={4} placeholder="Initial content (Markdown supported)..." value={form.content} onChange={e => setForm({...form, content: e.target.value})} />
              </div>
              <DialogFooter><Button onClick={create} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1 space-y-2">
          {boards.map(b => (
            <button key={b.id} onClick={() => setActive(b)}
                    className={`w-full text-left p-3 rounded-md border transition-colors ${active?.id === b.id ? "border-brand-aqua bg-brand-aqua-light" : "border-slate-200 bg-white hover:border-brand-aqua/40"}`}
                    data-testid={`board-item-${b.id}`}>
              <div className="flex items-center gap-2">
                <PenSquare className="w-4 h-4 text-slate-400" />
                <div className="text-sm font-medium text-slate-800">{b.title}</div>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">{formatDate(b.created_at)}</div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-3 kpi-card">
          {active ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="font-heading font-semibold text-xl text-slate-900">{active.title}</div>
                <Button size="sm" onClick={save} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="save-board-btn">Save</Button>
              </div>
              <Textarea
                value={active.content || ""}
                onChange={e => setActive({ ...active, content: e.target.value })}
                rows={20}
                className="font-mono text-sm min-h-[400px] bg-slate-50/50"
                placeholder="Start writing... Markdown formatting is supported."
                data-testid="board-content-textarea"
              />
            </div>
          ) : (
            <div className="text-slate-400 text-sm text-center py-12">Create or select a whiteboard.</div>
          )}
        </div>
      </div>
    </div>
  );
}
